import { useEffect } from "react"
import { useIntl } from "react-intl"
import { useNavigate } from "react-router-dom"

import { ICONS } from "@doktor-se/bones-ui"
import { IconButton } from "@doktor-se/bones-ui/dist/web-shared/components"
import { Staff } from "@doktor-se/bones-ui/dist/web-shared/types"

import { fetchPatientsClinics } from "api"
import { featureFlags } from "config"
import { useAppDispatch, useCarealotV2 } from "lib/hooks"
import { SearchPageConversation } from "types"

import CloseDialog from "components/CloseDialog/CloseDialog"
import PhysicalToDigitalDialog from "components/PhysicalToDigitalDialog/PhysicalToDigitalDialog"
import ToQueueDialog from "components/ToQueueDialog/ToQueueDialog"
import ReassignDialog from "components/v2/conversationList/components/ReassignDialog/ReassignDialog"
import ReassignDialogV2 from "components/v2/conversationList/components/ReassignDialog/ReassignDialog"
import { IconCloseConversation, IconReassign, IconReopenActive, IconToQueue } from "icons"

import styles from "./ConversationControls.module.scss"

export interface ConversationControlsProps {
  conversation: SearchPageConversation
  reopen: boolean
  showReopen: () => void
  closeControl: (refetch?: boolean) => void
  reopenConversation: (staffId: Staff["id"]) => void
  showClose: () => void
  close: boolean
  showToQueue: () => void
  toQueue: boolean
  showReassign: () => void
  reassign: boolean
  features: {
    hasAssignToOtherStaff: boolean
    hasAssignToQueue: boolean
    hasReopen: boolean
  }
}

const ConversationControls = ({
  conversation,
  reopen,
  showReopen,
  closeControl,
  showClose,
  close,
  showToQueue,
  toQueue,
  showReassign,
  reassign,
  features
}: ConversationControlsProps) => {
  const dispatch = useAppDispatch()
  const intl = useIntl()
  const { hasAssignToOtherStaff, hasAssignToQueue, hasReopen } = features
  const navigate = useNavigate()
  const newVersion = useCarealotV2()

  useEffect(() => {
    if (!featureFlags.has("physical_to_digital")) return

    // Digital: Always fetch patient clinics on component render
    // Clinics: Only fetch patient clinics if user clicked on toQueue action
    if (!newVersion || (newVersion && toQueue)) {
      dispatch(fetchPatientsClinics(conversation.patientId, conversation.profileId ?? undefined))
    }
  }, [newVersion, toQueue, conversation.patientId, conversation.profileId, dispatch])

  return (
    <div className={styles.container} data-testid="controls">
      {conversation.state === "opened" && (
        <>
          {hasAssignToOtherStaff && (
            <IconButton
              onClick={showReassign}
              Icon={IconReassign}
              label={intl.formatMessage({ id: "label.reassign" })}
            />
          )}

          {conversation.assignedStaffId && (
            <>
              {hasAssignToQueue && (
                <IconButton
                  onClick={showToQueue}
                  Icon={IconToQueue}
                  label={intl.formatMessage({ id: "label.to.queue" })}
                />
              )}

              <IconButton
                onClick={showClose}
                Icon={IconCloseConversation}
                label={intl.formatMessage({ id: "label.close" })}
              />
            </>
          )}
        </>
      )}

      {conversation.state === "closed" && hasReopen && (
        <IconButton onClick={showReopen} Icon={IconReopenActive} label={intl.formatMessage({ id: "label.reopen" })} />
      )}

      {reopen && (
        <ReassignDialogV2
          isOpen={reopen}
          conversation={conversation}
          onClose={closeControl}
          header={intl.formatMessage({ id: "label.errand.reopenTo" })}
        />
      )}

      {reassign &&
        (newVersion ? (
          <ReassignDialogV2 isOpen={reassign} onClose={closeControl} conversation={conversation} />
        ) : (
          <ReassignDialog isOpen={reassign} onClose={closeControl} conversation={conversation} />
        ))}

      {close && <CloseDialog isOpen={close} onClose={closeControl} conversation={conversation} />}

      {toQueue &&
        (featureFlags.has("physical_to_digital") ? (
          <PhysicalToDigitalDialog isOpen={toQueue} onClose={closeControl} conversation={conversation} />
        ) : (
          <ToQueueDialog isOpen={toQueue} conversation={conversation} onClose={closeControl} />
        ))}
      {featureFlags.has("errand_details_view") &&
        (conversation.state === "closed" || conversation.state === "booked") && (
          <IconButton
            onClick={() => navigate(`/search/${conversation.id}`)}
            Icon={ICONS.Visibility}
            label={intl.formatMessage({ id: "label.errand.view" })}
          />
        )}
    </div>
  )
}

export default ConversationControls
